import { GraphQL } from "@rpi/openapi-api";
import { dashboardUserRoleLabel } from "../../utils/graphql.util";

export const ONE_CUSTOMER_ADMIN_REQUIRED = "Each Organization requires at least one Customer Admin.";

// Dropdown order per the Account Settings comp: admin roles first, then User.
const ROLE_OPTION_ORDER: GraphQL.DashboardUserRole[] = [
  GraphQL.DashboardUserRole.CustomerAdmin,
  GraphQL.DashboardUserRole.SuperAdmin,
  GraphQL.DashboardUserRole.User,
];

export interface RoleOption {
  label: string;
  value: GraphQL.DashboardUserRole;
  disabled?: boolean;
  disabledReason?: string;
}

export type RoleFieldConfig = { mode: "readonly"; value: string } | { mode: "editable"; options: RoleOption[] };

/**
 * How the Account Settings Role field renders for the signed-in user. A User
 * cannot change their own role; admins get a dropdown scoped to roles they may
 * assign, with the last-Customer-Admin guard applied to the User option.
 */
export const roleFieldConfig = ({
  viewerRole,
  isLastCustomerAdmin,
}: {
  viewerRole: GraphQL.DashboardUserRole;
  isLastCustomerAdmin: boolean;
}): RoleFieldConfig => {
  if (viewerRole === GraphQL.DashboardUserRole.User) {
    return { mode: "readonly", value: dashboardUserRoleLabel(GraphQL.DashboardUserRole.User) };
  }

  const viewerIsSuperAdmin = viewerRole === GraphQL.DashboardUserRole.SuperAdmin;
  const assignableRoles = viewerIsSuperAdmin
    ? ROLE_OPTION_ORDER
    : ROLE_OPTION_ORDER.filter((role) => role !== GraphQL.DashboardUserRole.SuperAdmin);
  const options = assignableRoles.map((value) =>
    guardLastCustomerAdmin({ value, label: dashboardUserRoleLabel(value) }, isLastCustomerAdmin)
  );
  return { mode: "editable", options };
};

const guardLastCustomerAdmin = (
  option: { label: string; value: GraphQL.DashboardUserRole },
  isLastCustomerAdmin: boolean
): RoleOption => {
  if (isLastCustomerAdmin && option.value === GraphQL.DashboardUserRole.User) {
    return { ...option, disabled: true, disabledReason: ONE_CUSTOMER_ADMIN_REQUIRED };
  }
  return option;
};

type RoleHolder = { role?: GraphQL.DashboardUserRole | null } | null;

export const countCustomerAdmins = (users: ReadonlyArray<RoleHolder> | null | undefined): number =>
  (users ?? []).filter((user) => user?.role === GraphQL.DashboardUserRole.CustomerAdmin).length;

export const isOnlyCustomerAdmin = (users: ReadonlyArray<RoleHolder> | null | undefined): boolean =>
  countCustomerAdmins(users) === 1;
